import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MoreHorizontal,
  FileText,
  Download,
  Search,
  Calendar,
  BarChart2,
  RefreshCw,
  Filter,
  FileCheck,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar as CalendarIcon,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/use-toast';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { getAllBusinessContracts } from '@/services/business-opportunities';
import { formatCurrency } from '@/lib/utils';
import { ContractStatus, BusinessContract } from '@/types/business-opportunities';
import { Progress } from '@/components/ui/progress';

// Extended type to include customer property from the API response
type BusinessContractWithCustomer = BusinessContract & {
  customer?: {
    id: string;
    name: string;
  };
};

export default function Contracts() {
  const [contracts, setContracts] = useState<BusinessContractWithCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const statusMap: Record<ContractStatus, { label: string; variant: 'default' | 'outline' | 'secondary' | 'destructive' | 'success' | 'warning' }> = {
    draft: { label: 'Rascunho', variant: 'outline' },
    sent: { label: 'Enviado', variant: 'secondary' },
    signed: { label: 'Assinado', variant: 'success' },
    active: { label: 'Ativo', variant: 'success' },
    expired: { label: 'Expirado', variant: 'destructive' },
    canceled: { label: 'Cancelado', variant: 'destructive' },
  };
  
  const statusIcons: Record<ContractStatus, React.ReactNode> = {
    draft: <FileText className="h-4 w-4" />,
    sent: <Clock className="h-4 w-4" />,
    signed: <FileCheck className="h-4 w-4" />,
    active: <CheckCircle2 className="h-4 w-4" />,
    expired: <AlertCircle className="h-4 w-4" />,
    canceled: <XCircle className="h-4 w-4" />,
  };

  useEffect(() => {
    fetchContracts();
  }, []);

    const fetchContracts = async () => {
      try {
      setIsRefreshing(true);
      const data = await getAllBusinessContracts();
      setContracts(data);
      setLoading(false);
      } catch (error) {
        console.error('Error fetching contracts:', error);
        toast({
        title: 'Erro',
        description: 'Não foi possível carregar os contratos.',
          variant: 'destructive',
        });
      } finally {
      setIsRefreshing(false);
    }
  };

  const handleViewContract = (contractId: string) => {
    window.open(`/contract/${contractId}`, '_blank');
  };

  const handleDownloadPdf = (contract: BusinessContractWithCustomer) => {
    if (contract.pdf_url) {
      window.open(contract.pdf_url, '_blank');
    } else {
      toast({
        title: 'Indisponível',
        description: 'PDF não disponível para este contrato.',
        variant: 'destructive',
      });
    }
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      const matchesSearch = 
        contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        !selectedStatus || 
        contract.status === selectedStatus;
      
      return matchesSearch && matchesStatus;
    });
  }, [contracts, searchTerm, selectedStatus]);

  // Calculate stats
  const totalContracts = contracts.length;
  const activeContracts = contracts.filter(c => c.status === 'active').length;
  const expiredContracts = contracts.filter(c => c.status === 'expired').length;
  const totalValue = contracts
    .filter(c => c.status === 'active')
    .reduce((sum, contract) => sum + contract.total_value, 0);

  const contractsByStatus = [
    {
      status: 'draft',
      count: contracts.filter(c => c.status === 'draft').length,
      label: 'Rascunho',
    },
    {
      status: 'sent',
      count: contracts.filter(c => c.status === 'sent').length,
      label: 'Enviado',
    },
    {
      status: 'signed',
      count: contracts.filter(c => c.status === 'signed').length,
      label: 'Assinado',
    },
    {
      status: 'active',
      count: contracts.filter(c => c.status === 'active').length,
      label: 'Ativo',
    },
    {
      status: 'expired',
      count: contracts.filter(c => c.status === 'expired').length,
      label: 'Expirado',
    },
    {
      status: 'canceled',
      count: contracts.filter(c => c.status === 'canceled').length,
      label: 'Cancelado',
    },
  ];

  const percentageByStatus = contractsByStatus.map(status => ({
    ...status,
    percentage: totalContracts > 0 ? Math.round((status.count / totalContracts) * 100) : 0
  }));

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-extrabold tracking-tight">Contratos</h1>
        <Button 
          onClick={fetchContracts} 
          variant="outline" 
          size="sm"
          disabled={isRefreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total de Contratos</CardDescription>
            <CardTitle className="text-3xl">{totalContracts}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Todos os contratos</span>
              <FileText className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Contratos Ativos</CardDescription>
            <CardTitle className="text-3xl">{activeContracts}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{Math.round((activeContracts / (totalContracts || 1)) * 100)}% do total</span>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Valor Total (Ativos)</CardDescription>
            <CardTitle className="text-3xl">{formatCurrency(totalValue)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Apenas contratos ativos</span>
              <BarChart2 className="h-4 w-4" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Contratos Expirados</CardDescription>
            <CardTitle className="text-3xl">{expiredContracts}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Requer atenção</span>
              <AlertCircle className="h-4 w-4 text-red-500" />
        </div>
          </CardContent>
        </Card>
      </div>

      {/* Status distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Distribuição por Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {percentageByStatus.map((item) => (
              <div key={item.status} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    {statusIcons[item.status as ContractStatus]}
                    <span className="ml-2">{item.label}</span>
                  </div>
                  <span>{item.count} ({item.percentage}%)</span>
                </div>
                <Progress value={item.percentage} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main content */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
                  type="search"
            placeholder="Buscar contratos..."
                  className="pl-8 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
              </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  <Filter className="h-4 w-4 mr-2" />
                  Status
                  {selectedStatus && (
                    <Badge variant="secondary" className="ml-2">
                      {statusMap[selectedStatus as ContractStatus]?.label || selectedStatus}
                    </Badge>
                  )}
            </Button>
          </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setSelectedStatus(null)}>
                  Todos os status
            </DropdownMenuItem>
                <DropdownMenuSeparator />
                {Object.entries(statusMap).map(([status, {label}]) => (
              <DropdownMenuItem 
                key={status} 
                    onClick={() => setSelectedStatus(status)}
                  >
                    <div className="flex items-center">
                      {statusIcons[status as ContractStatus]}
                      <span className="ml-2">{label}</span>
                    </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-muted-foreground">
                Nenhum contrato encontrado.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Início</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow 
                      key={contract.id}
                      className="cursor-pointer"
                      onClick={() => handleViewContract(contract.id)}
                    >
                      <TableCell className="font-extrabold">{contract.title}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>
                              {contract.customer?.name?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <span>{contract.customer?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={statusMap[contract.status].variant}
                          className="flex items-center gap-1 w-fit"
                        >
                          {statusIcons[contract.status]}
                          {statusMap[contract.status].label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                          {format(new Date(contract.start_date), 'dd/MM/yyyy')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-extrabold">
                        {formatCurrency(contract.total_value)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleViewContract(contract.id);
                            }}>
                              <FileText className="mr-2 h-4 w-4" />
                              Ver detalhes
                            </DropdownMenuItem>
                            {contract.pdf_url && (
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPdf(contract);
                              }}>
                                <Download className="mr-2 h-4 w-4" />
                                Baixar PDF
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {filteredContracts.length} de {contracts.length} contratos
          </div>
        </CardFooter>
      </Card>
    </div>
  );
} 